import type { VendorProfile } from './vendorApiClient';

export interface ProductCounts {
  total: number;
  live: number;
}

/**
 * Setup-wizard progress (Figma §9, re-designed). The 7 steps: the application's
 * four data sections (Business · Location & hours · Systems & eligibility · Your
 * Interests) arrive PRE-FILLED ✓ from the reviewed application and remain
 * view-only — then the vendor starts at Storefront → Import → Confirm. Progress
 * is DERIVED FROM DATA PRESENCE, not a state machine. Storefront/Import/Confirm
 * are the live gates that flip the store to active and hide the SetupBanner.
 */

export type SetupStepKey = 'business' | 'location' | 'systems' | 'interests' | 'storefront' | 'import' | 'confirm';

/** prefilledView = ✓ from the reviewed application; active = the vendor does it now. */
export type SetupStepKind = 'prefilledView' | 'active';

export interface SetupStep {
  key: SetupStepKey;
  kind: SetupStepKind;
  complete: boolean;
}

export interface SetupProgress {
  steps: SetupStep[];
  /** Completed steps across all 7 (drives the "X of 7" chip). */
  completed: number;
  total: number;
  /** Storefront + Import + Confirm all complete → store can go live; banner hides. */
  allRequiredDone: boolean;
  /** Index into `steps` of the first incomplete step the vendor can act on. */
  firstIncomplete: number;
}

export function computeSetup(profile: VendorProfile | null, products: ProductCounts | null): SetupProgress {
  const total = products?.total ?? 0;
  const liveCount = products?.live ?? 0;
  const sf = profile?.photosJson ?? null;

  const profileDone = !!profile && !!profile.businessName && !!profile.businessType && !!profile.address;
  const storefrontDone = !!sf && !!(sf.logo || (sf.photos && sf.photos.length));
  const importDone = total > 0;
  const confirmDone = liveCount > 0;

  const steps: SetupStep[] = [
    { key: 'business', kind: 'prefilledView', complete: profileDone },
    { key: 'location', kind: 'prefilledView', complete: !!profile?.address },
    { key: 'systems', kind: 'prefilledView', complete: !!profile },
    { key: 'interests', kind: 'prefilledView', complete: true },
    { key: 'storefront', kind: 'active', complete: storefrontDone },
    { key: 'import', kind: 'active', complete: importDone },
    { key: 'confirm', kind: 'active', complete: confirmDone },
  ];

  const completed = steps.filter((s) => s.complete).length;
  const required = steps.filter((s) => s.kind === 'active');
  const allRequiredDone = required.every((s) => s.complete);
  // First incomplete the vendor can act on — start the wizard there (Storefront onward).
  const firstActiveIncomplete = steps.findIndex((s) => s.kind === 'active' && !s.complete);

  return {
    steps,
    completed,
    total: steps.length,
    allRequiredDone,
    firstIncomplete: firstActiveIncomplete === -1 ? steps.length - 1 : firstActiveIncomplete,
  };
}
